import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { isBodyScrollLocked } from '../utils/bodyScrollLock';

/** Pages catalogue (Acheter) avec navbar auto-masquée au scroll. */
export const CATALOG_SCROLL_PATH = /^\/(acheter|immobilier-entreprise)/;

/** Pages où la navbar se masque au scroll (catalogue + fiche programme). */
export const AUTO_HIDE_NAV_PATH = /^\/(acheter|immobilier-entreprise|projet)/;

export const PROJECT_DETAIL_PATH = /^\/projet\//;

type ScrollNavContextValue = {
  navVisible: boolean;
  isCatalogPage: boolean;
  isProjectDetailPage: boolean;
  isAutoHideNavPage: boolean;
};

const ScrollNavContext = createContext<ScrollNavContextValue>({
  navVisible: true,
  isCatalogPage: false,
  isProjectDetailPage: false,
  isAutoHideNavPage: false,
});

export const ScrollNavProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { pathname } = useLocation();
  const isCatalogPage = CATALOG_SCROLL_PATH.test(pathname);
  const isProjectDetailPage = PROJECT_DETAIL_PATH.test(pathname);
  const isAutoHideNavPage = AUTO_HIDE_NAV_PATH.test(pathname);
  const [navVisible, setNavVisible] = useState(true);
  const lastY = useRef(0);
  const navVisibleRef = useRef(true);
  const rafRef = useRef(0);

  const setNavVisibleIfChanged = (next: boolean) => {
    if (next === navVisibleRef.current) return;
    navVisibleRef.current = next;
    setNavVisible(next);
  };

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.projectDetailPage = isProjectDetailPage ? '1' : '0';
    root.dataset.siteNavVisible = navVisible ? '1' : '0';
  }, [isProjectDetailPage, navVisible]);

  useEffect(() => {
    if (!isAutoHideNavPage) {
      setNavVisibleIfChanged(true);
      lastY.current = window.scrollY;
      return;
    }

    lastY.current = window.scrollY;

    const updateNavVisibility = () => {
      if (isBodyScrollLocked()) {
        return;
      }
      const y = window.scrollY;
      if (y < 72) {
        setNavVisibleIfChanged(true);
      } else if (y > lastY.current + 10) {
        setNavVisibleIfChanged(false);
      } else if (y < lastY.current - 10) {
        setNavVisibleIfChanged(true);
      }
      lastY.current = y;
    };

    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateNavVisibility);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('scroll', onScroll);
    };
  }, [isAutoHideNavPage, pathname]);

  const contextValue = useMemo(
    () => ({ navVisible, isCatalogPage, isProjectDetailPage, isAutoHideNavPage }),
    [navVisible, isCatalogPage, isProjectDetailPage, isAutoHideNavPage],
  );

  return (
    <ScrollNavContext.Provider value={contextValue}>
      {children}
    </ScrollNavContext.Provider>
  );
};

export function useScrollNav(): ScrollNavContextValue {
  return useContext(ScrollNavContext);
}
